import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PromoBanner from '../components/PromoBanner';
import { CheckCircle, Clock, Book, Code, Award, ArrowRight } from 'lucide-react';

const CloudSupportEngineerPath = () => {
  const [expandedModule, setExpandedModule] = useState(null);

  const learningPath = {
    title: "Cloud Support Engineer",
    description: "Launch your career as a Cloud Support Engineer with this comprehensive learning path. Master real AWS projects and get certified with our curated courses.",
    duration: "3-4 months",
    difficulty: "Beginner to Intermediate",
    outcomes: [
      "Complete AWS Cloud Practitioner certification",
      "Build 10+ hands-on projects",
      "Master troubleshooting techniques for AWS services", 
      "Prepare for cloud support job interviews"
    ],
    modules: [
      {
        id: 1,
        title: "Cloud Fundamentals",
        duration: "2-3 weeks",
        description: "Build your foundation in cloud computing concepts",
        courses: [
          "Study Notes: AWS Certified Cloud Practitioner",
          "Free Project: AWS Cloud Fun Facts Generator"
        ],
        skills: ["Cloud Computing Basics", "AWS Core Services", "Cloud Economics"],
        projects: ["Complete AWS Cloud Fun Facts Generator", "Practice with AWS Free Tier"]
      },
      {
        id: 2,
        title: "Cloud Support Projects",
        duration: "4-5 weeks", 
        description: "Gain hands-on experience with cloud support scenarios",
        courses: [
          "5 AWS Cloud Projects to Become a Cloud Support Engineer",
          "Build: 5 Beginner AWS Cloud Projects",
          "Build: 5 Intermediate AWS Cloud Projects"
        ],
        skills: ["Troubleshooting", "Customer Communication", "Issue Resolution"],
        projects: ["Complete 5 cloud support projects", "Build beginner-level cloud solutions", "Build intermediate-level cloud solutions"]
      },
      {
        id: 3,
        title: "Certification Preparation",
        duration: "3-4 weeks",
        description: "Prepare for AWS Cloud Practitioner certification",
        courses: [
          "AWS Certified Cloud Practitioner Practice Tests"
        ],
        skills: ["Exam Preparation", "AWS Best Practices", "Cloud Security Basics"],
        projects: ["Take multiple practice exams", "Review AWS documentation"]
      },
      {
        id: 4,
        title: "Career Development",
        duration: "2-3 weeks",
        description: "Prepare for job applications and interviews",
        courses: [
          "AWS Interview Mastery"
        ],
        skills: ["Interview Skills", "Resume Building", "Career Strategy"],
        projects: ["Optimize LinkedIn profile", "Practice technical interviews"]
      }
    ],
    certifications: [
      "AWS Certified Cloud Practitioner (Recommended first)",
      "AWS Certified Solutions Architect - Associate (Advanced)"
    ],
    careerOutlook: {
      averageSalary: "$55,000 - $85,000",
      jobGrowth: "15% annually",
      topSkills: ["AWS Services", "Troubleshooting", "Customer Service", "Linux/Windows"]
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PromoBanner />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Award size={16} />
              Career Learning Path
            </div>
            <div className="mb-6">
              <img 
                src="/course-covers/cloud-support-engineer.png"
                alt="Cloud Support Engineer"
                className="w-32 h-32 object-contain mx-auto"
              />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {learningPath.title}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {learningPath.description}
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="flex items-center gap-2 bg-white px-4 py-3 rounded-lg shadow-sm">
                <Clock size={20} className="text-blue-500" />
                <span className="font-medium">{learningPath.duration}</span>
              </div>
            </div>
            
            <a 
              href="https://learn.zerotocloud.co/p/learning-path-cloud-support-engineer"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-200 inline-block text-center"
            >
              Start Learning Path
            </a>
          </div>
        </div>
      </section>

      {/* What is a Cloud Support Engineer */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                What is a Cloud Support Engineer?
              </h2>
              <div className="prose prose-lg mx-auto text-center">
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  A Cloud Support Engineer is a technical professional who helps customers resolve issues with cloud infrastructure and services. They serve as the bridge between complex cloud technologies and users who need assistance.
                </p>
                <div className="grid md:grid-cols-2 gap-8 mt-8">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Daily Responsibilities</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        Respond to customer support tickets
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        Troubleshoot cloud infrastructure issues
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        Guide customers through problem resolution
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        Monitor system health and performance
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Work Environment</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        Remote-friendly positions available
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        Collaborative team environment
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        Opportunity for career advancement
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        Continuous learning and skill development
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Outcomes */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              What You'll Learn
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {learningPath.outcomes.map((outcome, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">{outcome}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Learning Modules */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Learning Modules
            </h2>
            
            <div className="space-y-6">
              {learningPath.modules.map((module, index) => (
                <div key={module.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div 
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedModule(expandedModule === module.id ? null : module.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-lg flex items-center justify-center font-bold text-lg">
                          {index + 1}
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-1">
                            {module.title}
                          </h3>
                          <p className="text-gray-600 text-sm">{module.duration}</p>
                        </div>
                      </div>
                      <ArrowRight 
                        size={20} 
                        className={`text-gray-400 transition-transform ${
                          expandedModule === module.id ? 'rotate-90' : ''
                        }`}
                      />
                    </div>
                    <p className="text-gray-600 mt-4">{module.description}</p>
                  </div>
                  
                  {expandedModule === module.id && (
                    <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                      <div className="grid md:grid-cols-2 gap-6 mt-6">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <Book size={16} />
                            Courses
                          </h4>
                          <ul className="space-y-2">
                            {module.courses.map((course, i) => (
                              <li key={i} className="text-gray-600 text-sm flex items-center gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                {course}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <Code size={16} />
                            Key Skills
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {module.skills.map((skill, i) => (
                              <span 
                                key={i} 
                                className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6">
                        <h4 className="font-semibold text-gray-900 mb-3">Hands-on Projects</h4>
                        <ul className="space-y-1">
                          {module.projects.map((project, i) => (
                            <li key={i} className="text-gray-600 text-sm flex items-center gap-2">
                              <CheckCircle size={14} className="text-green-500" />
                              {project}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Get Started Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Ready to get started?
            </h2>
            
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Left Side - Learning Path Info */}
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    🎯 Learning Path Overview
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Clock size={20} className="text-blue-500" />
                      <span className="text-gray-700">
                        <strong>Duration:</strong> {learningPath.duration}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Book size={20} className="text-green-500" />
                      <span className="text-gray-700">
                        <strong>Total Courses:</strong> 7 comprehensive courses
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Award size={20} className="text-purple-500" />
                      <span className="text-gray-700">
                        <strong>Certification:</strong> AWS Cloud Practitioner prep included
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-teal-500" />
                      <span className="text-gray-700">
                        <strong>Projects:</strong> 10+ hands-on projects
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 border border-green-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    📈 What You'll Achieve
                  </h3>
                  <ul className="space-y-3">
                    {learningPath.outcomes.map((outcome, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                        <span className="text-gray-700 text-sm">{outcome}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Right Side - Course Thumbnails with Scroll */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                  📚 Courses Included
                </h3>
                
                <div className="h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 space-y-2 pr-2">
                  {/* Course 1 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/7.png" 
                        alt="AWS Cloud Practitioner Study Notes"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              Study Notes: AWS Certified Cloud Practitioner
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">$39</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course 2 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/6.png" 
                        alt="AWS Cloud Fun Facts Generator"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              Free Project: AWS Cloud Fun Facts Generator
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-green-600">Free</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course 3 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/2.png" 
                        alt="Beginner AWS Projects"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              Build: 5 Beginner AWS Cloud Projects
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">$69</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course 4 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/cloud-support-engineer-projects.jpg" 
                        alt="Cloud Support Engineer Projects"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              5 AWS Cloud Projects to Become a Cloud Support Engineer
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">$69</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course 5 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/7.png" 
                        alt="Practice Tests"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              AWS Certified Cloud Practitioner Practice Tests
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">$29</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course 6 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/1.png" 
                        alt="AWS Interview Mastery"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              AWS Interview Mastery
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">$129</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course 7 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/6.png" 
                        alt="Intermediate AWS Cloud Projects"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              Build: 5 Intermediate AWS Cloud Projects
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">$79</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Pricing Section at Bottom */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Ready to get started?
            </h2>
            
            {/* Centered Pricing */}
            <div className="max-w-md mx-auto">
              <div className="text-center space-y-4">
                <button className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-200">
                  Start Learning Path - $299
                </button>
                <div className="text-sm text-gray-500 font-medium">Complete Learning Path</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default CloudSupportEngineerPath;